import { useCallback, useEffect, useState } from 'react';
import { gamesApi } from '../../api/games';
import { adminApi } from '../../api/admin';
import { useUiStore } from '../../store/uiStore';
import { Spinner } from '../../components/Spinner';

type GameSlug = 'slot' | 'roulette' | 'blackjack';

const GAMES: { slug: GameSlug; name: string }[] = [
  { slug: 'slot', name: 'Slot Machine' },
  { slug: 'roulette', name: 'Roulette' },
  { slug: 'blackjack', name: 'Blackjack' },
];

export function AdminGames() {
  const pushToast = useUiStore((s) => s.pushToast);

  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);

  // Maintenance mode current state is not exposed by the read APIs, so we
  // default the toggle to off and let the admin set it explicitly.
  const [maintenance, setMaintenance] = useState(false);
  const [maintenanceBusy, setMaintenanceBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const res = await gamesApi.listGames();
      const map: Record<string, boolean> = {};
      for (const game of res.games) map[game.slug] = true;
      setEnabled(map);
    } catch (err) {
      setErrorMsg(
        err instanceof Error ? err.message : 'Failed to load games.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleToggleGame(slug: GameSlug) {
    const next = !enabled[slug];
    setBusySlug(slug);
    try {
      await adminApi.toggleGame(slug, next);
      setEnabled((prev) => ({ ...prev, [slug]: next }));
      pushToast('success', `${slug} ${next ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      pushToast(
        'error',
        err instanceof Error ? err.message : 'Toggle failed.',
      );
    } finally {
      setBusySlug(null);
    }
  }

  async function handleToggleMaintenance() {
    const next = !maintenance;
    setMaintenanceBusy(true);
    try {
      await adminApi.toggleMaintenance(next);
      setMaintenance(next);
      pushToast('success', `Maintenance mode ${next ? 'on' : 'off'}.`);
    } catch (err) {
      pushToast(
        'error',
        err instanceof Error ? err.message : 'Toggle failed.',
      );
    } finally {
      setMaintenanceBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-100">Games</h1>

      {loading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : errorMsg ? (
        <p className="card text-sm text-rose-400">{errorMsg}</p>
      ) : (
        <div className="space-y-3">
          {GAMES.map((game) => {
            const isEnabled = Boolean(enabled[game.slug]);
            return (
              <div
                key={game.slug}
                className="card flex items-center justify-between"
              >
                <div>
                  <p className="font-semibold text-slate-100">{game.name}</p>
                  <p
                    className={`text-sm ${
                      isEnabled ? 'text-emerald-400' : 'text-slate-500'
                    }`}
                  >
                    {isEnabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <button
                  type="button"
                  className={isEnabled ? 'btn-ghost' : 'btn-gold'}
                  onClick={() => handleToggleGame(game.slug)}
                  disabled={busySlug === game.slug}
                >
                  {busySlug === game.slug ? (
                    <Spinner />
                  ) : isEnabled ? (
                    'Disable'
                  ) : (
                    'Enable'
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <section className="card flex items-center justify-between">
        <div>
          <p className="font-semibold text-slate-100">Maintenance mode</p>
          <p className="text-sm text-slate-400">
            When on, players are blocked from playing. Current state is not
            read from the server — set it explicitly.
          </p>
          <p
            className={`text-sm ${
              maintenance ? 'text-rose-400' : 'text-emerald-400'
            }`}
          >
            {maintenance ? 'On' : 'Off'}
          </p>
        </div>
        <button
          type="button"
          className={maintenance ? 'btn-ghost' : 'btn-gold'}
          onClick={handleToggleMaintenance}
          disabled={maintenanceBusy}
        >
          {maintenanceBusy ? <Spinner /> : maintenance ? 'Turn off' : 'Turn on'}
        </button>
      </section>
    </div>
  );
}
